/**
 * Status Message Component
 *
 * Displays a centered status message with an image, headline, and description.
 * Used for special events like empty search results or HTTP errors.
 */

import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Event images
import noItemsImage from '../../assets/light/no-items.png';
import error500Image from '../../assets/light/error-500.png';

export type StatusEvent = 'no-results' | 'server-error';

const STATUS_IMAGES: Record<StatusEvent, string> = {
  'no-results': noItemsImage,
  'server-error': error500Image,
};

// Translation keys for each event
const STATUS_TRANSLATION_KEYS: Record<StatusEvent, { titleKey: string; descKey: string; ns: string }> = {
  'no-results': {
    titleKey: 'results.noResults.title',
    descKey: 'results.noResults.description',
    ns: 'search',
  },
  'server-error': {
    titleKey: 'errors.serverError.title',
    descKey: 'errors.serverError.description',
    ns: 'common',
  },
};

interface StatusMessageProps {
  event: StatusEvent;
}

export function StatusMessage({ event }: StatusMessageProps) {
  const keys = STATUS_TRANSLATION_KEYS[event];
  const { t } = useTranslation(keys.ns);
  const image = STATUS_IMAGES[event];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* Image */}
      <Box
        component="img"
        src={image}
        alt=""
        sx={{
          width: '30%',
          maxWidth: 500,
          height: 'auto',
          mb: 4,
        }}
      />

      {/* Headline */}
      <Typography
        variant="h6"
        sx={{
          color: '#F15A24',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: 1,
          mb: 1,
        }}
      >
        {t(keys.titleKey)}
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{
          color: 'primary.main',
        }}
      >
        {t(keys.descKey)}
      </Typography>
    </Box>
  );
}
